import {
  SaveToResultsStepDataName,
  SINGLE_DATASET_OUTPUT_STEP_DATA_NAME,
} from 'analysis-tool/AnalysisStepDataNames';
import {PARAMETER_OPTIMIZATION_STEP_TYPES} from 'analysis-tool/AnalysisToolConstants';
import {GetAnalysisPipelineStep} from 'analysis-tool/AnalysisTypes';
import {getIconAndTextForChartViewStep} from 'analysis-tool/charting/AnalysisChartingUtils';
import {AnalysisMainViewItem, AnalysisMainViewItemType} from 'analysis-tool/ui/main-view/MainViewTypes';
import {assertAndContinue} from 'Asserts';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {
  PipelineStepDataKind,
  PipelineStepStatusCode,
  PipelineStepType,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import {getPipelineStepConfig} from 'pipelines/StepShapeUtils';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import APIUtil from 'util/APIUtil';
import pickDefined from 'util/pickDefined';

interface SidebarItemData {
  icon: BenchlingIconName;
  text: string;
  /** Main View item corresponding to this Sidebar item. Used for determining (a) which Main View item to
   * open when the Sidebar item is selected, and (b) if the Sidebar Item is already selected for rendering
   * purposes.
   */
  mainViewItem: AnalysisMainViewItem;
}

const BASE_FAILED_STEP_SIDEBAR_ITEM_DATA: Omit<SidebarItemData, 'mainViewItem'> = {
  icon: 'request-failed',
  text: 'Error',
};

/** Step types for which only custom, non-output sidebar items should be shown. */
const STEP_TYPES_WITH_HIDDEN_OUTPUTS: Array<PipelineStepType> = [
  ...PARAMETER_OPTIMIZATION_STEP_TYPES,
  PipelineStepType.EXPORT_DATA_CATALOG_DATASET,
  PipelineStepType.SAVE_TO_RESULTS,
  PipelineStepType.DATASET_VIEW,
];

/** Map of step types to failed view generator functions for step types that don't use the default AnalysisMainViewItemType.FAILED view. */
const STEP_TYPES_TO_FAILED_VIEW_GENERATOR: {
  [pipelineStepType in PipelineStepType]?: (step: GetAnalysisPipelineStep) => SidebarItemData;
} = {
  [PipelineStepType.SAVE_TO_RESULTS]: (step: GetAnalysisPipelineStep) => ({
    ...BASE_FAILED_STEP_SIDEBAR_ITEM_DATA,
    mainViewItem: {type: AnalysisMainViewItemType.SAVE_TO_RESULTS_OUTPUT, stepId: step.id},
  }),
  [PipelineStepType.THIRD_PARTY]: (step: GetAnalysisPipelineStep) => ({
    ...BASE_FAILED_STEP_SIDEBAR_ITEM_DATA,
    mainViewItem: {type: AnalysisMainViewItemType.THIRD_PARTY_STEP, stepId: step.id},
  }),
};

function getFailedStepSidebarItemData(step: GetAnalysisPipelineStep): SidebarItemData {
  const customFailedStatusHandler = STEP_TYPES_TO_FAILED_VIEW_GENERATOR[step.definition.stepType];

  if (customFailedStatusHandler !== undefined) {
    return customFailedStatusHandler(step);
  }

  return {
    ...BASE_FAILED_STEP_SIDEBAR_ITEM_DATA,
    mainViewItem: {
      type: AnalysisMainViewItemType.FAILED,
      stepId: step.id,
    },
  };
}

/** @package */
export function getNonOutputSidebarItemDatasForStep(step: GetAnalysisPipelineStep): Array<SidebarItemData> {
  if (step.statusCode === PipelineStepStatusCode.RUNNING) {
    return [
      {
        icon: 'request-in-progress',
        text: 'Loading...',
        mainViewItem: {
          type: AnalysisMainViewItemType.RUNNING,
          stepId: step.id,
        },
      },
    ];
  }

  if (step.statusCode === PipelineStepStatusCode.FAILED) {
    return [getFailedStepSidebarItemData(step)];
  }

  if (
    step.definition.stepType === PipelineStepType.THIRD_PARTY &&
    step.statusCode !== PipelineStepStatusCode.SUCCEEDED
  ) {
    switch (step.statusCode) {
      case PipelineStepStatusCode.WAITING:
        return [
          {
            icon: 'request-in-progress',
            text: 'Awaiting output',
            mainViewItem: {type: AnalysisMainViewItemType.THIRD_PARTY_STEP, stepId: step.id},
          },
        ];
      default:
        assertAndContinue(
          false,
          'Unexpected status for THIRD_PARTY_STEP',
          pickDefined(step, 'statusCode', 'definition')
        );
        return [];
    }
  } else if (step.definition.stepType === PipelineStepType.PARAMETER_OPTIMIZATION_DATASET_CONFIGURATION) {
    switch (step.statusCode) {
      case PipelineStepStatusCode.SUCCEEDED:
        return [
          {
            icon: ModelTypeConstants.ANALYTICS_DASHBOARD,
            text: 'Data summary',
            mainViewItem: {
              type: AnalysisMainViewItemType.PARAMETER_OPTIMIZATION_DATASET_CONFIGURATION_DASHBOARD,
              stepId: step.id,
            },
          },
        ];
      default:
        assertAndContinue(
          false,
          'Unexpected status for PARAMETER_OPTIMIZATION_MODELING',
          pickDefined(step, 'statusCode', 'definition')
        );
        return [];
    }
  } else if (step.definition.stepType === PipelineStepType.PARAMETER_OPTIMIZATION_MODELING) {
    switch (step.statusCode) {
      case PipelineStepStatusCode.BLOCKED:
        return [];
      case PipelineStepStatusCode.SUCCEEDED:
        return [
          {
            icon: 'document',
            text: 'Overview',
            mainViewItem: {
              type: AnalysisMainViewItemType.PARAMETER_OPTIMIZATION_MODELING_OVERVIEW,
              stepId: step.id,
            },
          },
          {
            icon: ModelTypeConstants.ANALYTICS_DASHBOARD,
            text: 'Analysis results',
            mainViewItem: {
              type: AnalysisMainViewItemType.PARAMETER_OPTIMIZATION_MODELING_DASHBOARD,
              stepId: step.id,
            },
          },
        ];
      default:
        assertAndContinue(
          false,
          'Unexpected status for PARAMETER_OPTIMIZATION_MODELING',
          pickDefined(step, 'statusCode', 'definition')
        );
        return [];
    }
  } else if (step.definition.stepType === PipelineStepType.PARAMETER_OPTIMIZATION_PREDICTION) {
    switch (step.statusCode) {
      case PipelineStepStatusCode.BLOCKED:
        return [];
      case PipelineStepStatusCode.SUCCEEDED:
        return [
          {
            icon: 'crystal-ball',
            text: 'Predictions',
            mainViewItem: {type: AnalysisMainViewItemType.PARAMETER_OPTIMIZATION_PREDICTION, stepId: step.id},
          },
        ];
      default:
        assertAndContinue(
          false,
          'Unexpected status for PARAMETER_OPTIMIZATION_PREDICTION',
          pickDefined(step, 'statusCode', 'definition')
        );
        return [];
    }
  } else if (step.definition.stepType === PipelineStepType.EXPORT_DATA_CATALOG_DATASET) {
    switch (step.statusCode) {
      case PipelineStepStatusCode.SUCCEEDED: {
        const dataFrame = step.outputStepDatas.find(
          (stepData) => stepData.name === SINGLE_DATASET_OUTPUT_STEP_DATA_NAME
        )!.dataFrames[0];
        return [
          {
            icon: 'dataset',
            text: dataFrame.name,
            mainViewItem: {
              type: AnalysisMainViewItemType.EXPORT_DATA_CATALOG_DATASET_OUTPUT,
              stepId: step.id,
              dataFrameId: dataFrame.id,
            },
          },
        ];
      }
      default:
        assertAndContinue(
          false,
          'Unexpected status for EXPORT_DATA_CATALOG_DATASET',
          pickDefined(step, 'statusCode', 'definition')
        );
        return [];
    }
  } else if (step.definition.stepType === PipelineStepType.SAVE_TO_RESULTS) {
    switch (step.statusCode) {
      case PipelineStepStatusCode.SUCCEEDED:
        return [
          {
            icon: 'table',
            text: step.outputStepDatas.find(
              (stepData) => stepData.name === SaveToResultsStepDataName.SAVED_RESULTS
            )!.dataFrames[0].name,
            mainViewItem: {type: AnalysisMainViewItemType.SAVE_TO_RESULTS_OUTPUT, stepId: step.id},
          },
        ];
      default:
        assertAndContinue(
          false,
          'Unexpected status for SAVE_TO_RESULTS',
          pickDefined(step, 'statusCode', 'definition')
        );
        return [];
    }
  } else if (step.definition.stepType === PipelineStepType.SEND_TO_DOCUMENT) {
    const documentId = getPipelineStepConfig(step, PipelineStepType.SEND_TO_DOCUMENT, 0).documentId;
    const icon = APIUtil.apiIdToType(documentId) === ModelTypeConstants.WORKSHEET ? 'worksheet' : 'entry';
    return [
      {
        icon,
        // Only mainViewItem is actually used, so we don't need to set text properly
        text: '',
        mainViewItem: {type: AnalysisMainViewItemType.SEND_TO_DOCUMENT, stepId: step.id},
      },
    ];
    // Keep in sync with `client/analysis-tool/ui/sidebar/SidebarItemDatas.ts`
  } else if (step.definition.stepType === PipelineStepType.DATASET_VIEW) {
    switch (step.statusCode) {
      case PipelineStepStatusCode.SUCCEEDED:
        return [
          {
            mainViewItem: {
              type: AnalysisMainViewItemType.EDIT_CHART,
              stepId: step.id,
            },
            text: 'Unfinished view...',
            icon: ModelTypeConstants.DATA_FRAME,
          },
        ];
      default:
        assertAndContinue(
          false,
          'Unexpected status for DATASET_VIEW',
          pickDefined(step, 'statusCode', 'definition')
        );
    }
  }

  return [];
}

/**
 * Return sidebar items for the step outputs
 *
 * Currently, this returns:
 *   - 1 item per output Data Frame
 *   - 1 item (total) for all Files
 *
 * For step types in `STEP_TYPES_WITH_HIDDEN_OUTPUTS`, no sidebar items are returned by this function.
 * (Instead, custom views are returned by `getNonOutputSidebarItemDatasForStep`.)
 *
 * However, as we support these new cases, we may change this:
 *   - Step data with `DATASET isMulti: true` - we may want to render 1 item for all Data Frames, instead of 1
 *     per Data Frame, to group those Data Frames
 *   - Multiple step datas per step - we may want to group the data differently than the current behavior,
 *     since the current behavior doesn't group by step data
 *
 * This alternative could look like a `mainViewItem` of `{type: OUTPUT_STEP_DATA, stepDataId}` instead of the
 * current `DATA_FRAME` and `FILE_DASHBOARD` items.
 */
function getStepOutputSidebarItemDatas(step: GetAnalysisPipelineStep): Array<SidebarItemData> {
  if (step.statusCode !== PipelineStepStatusCode.SUCCEEDED) {
    return [];
  }

  if (STEP_TYPES_WITH_HIDDEN_OUTPUTS.some((stepType) => stepType === step.definition.stepType)) {
    return [];
  }

  // [Output Charts and Data Frames] 1 sidebar item per Data Frame
  const itemDatas: Array<SidebarItemData> = step.outputStepDatas
    .filter((stepData) => stepData.kind === PipelineStepDataKind.CHART)
    .flatMap((stepData) =>
      stepData.charts.map((chart) => ({
        mainViewItem: {
          type: AnalysisMainViewItemType.CHART,
          stepId: step.id,
        } as AnalysisMainViewItem,
        text: getIconAndTextForChartViewStep(step).title,
        icon: getIconAndTextForChartViewStep(step).icon,
      }))
    )
    .concat(
      step.outputStepDatas
        .filter((stepData) => stepData.kind === PipelineStepDataKind.DATASET)
        .flatMap((stepData) =>
          stepData.dataFrames.map((dataFrame) => ({
            mainViewItem: {
              type: AnalysisMainViewItemType.DATA_FRAME,
              dataFrameId: dataFrame.id,
              stepId: step.id,
            },
            text: dataFrame.name,
            icon: ModelTypeConstants.DATA_FRAME,
          }))
        )
    );

  // [Output Files] 1 sidebar item (total) for all Files
  if (step.outputStepDatas.some((stepData) => stepData.kind === PipelineStepDataKind.FILE)) {
    itemDatas.push({
      mainViewItem: {
        type: AnalysisMainViewItemType.FILE_DASHBOARD,
        stepId: step.id,
      },
      text: 'Files',
      icon: ModelTypeConstants.FILE,
    });
  }
  return itemDatas;
}

/**
 * Get all sidebar item datas associated with the input pipeline step.
 *
 * The list is generated from a concatenation of:
 * - Any step-type specific sidebar items, as defined in getNonOutputSidebarItemDatasForStep
 * - The step's output datas, if any.
 *
 * @deprecated
 */
export default function getStepSidebarItemDatas(step: GetAnalysisPipelineStep): Array<SidebarItemData> {
  return [...getNonOutputSidebarItemDatasForStep(step), ...getStepOutputSidebarItemDatas(step)];
}
